import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Plus,
  Edit,
  Trash2,
  ShieldAlert,
  Star,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { ClientTypeRecord } from "@shared/schema";

type Policy = {
  id: string;
  name: string;
  body: string;
  isDefault: boolean;
  defaultForClientTypes: string[] | null;
  isActive: boolean;
};

const EMPTY_FORM = {
  name: "",
  body: "",
  isDefault: false,
  defaultForClientTypes: [] as string[],
  isActive: true,
};

export function CancellationPoliciesCard() {
  const { toast } = useToast();
  const { data: policies = [], isLoading } = useQuery<Policy[]>({
    queryKey: ["/api/cancellation-policies"],
  });
  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
  });

  const [editing, setEditing] = useState<Policy | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Policy | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setCreating(true);
  };
  const openEdit = (p: Policy) => {
    setForm({
      name: p.name,
      body: p.body,
      isDefault: !!p.isDefault,
      defaultForClientTypes: p.defaultForClientTypes ?? [],
      isActive: p.isActive !== false,
    });
    setEditing(p);
  };

  const saveMutation = useMutation({
    mutationFn: async (payload: typeof EMPTY_FORM & { id?: string }) => {
      const url = payload.id
        ? `/api/cancellation-policies/${payload.id}`
        : "/api/cancellation-policies";
      const method = payload.id ? "PATCH" : "POST";
      const { id: _id, ...body } = payload;
      const res = await apiRequest(method, url, body);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/cancellation-policies"] });
      setCreating(false);
      setEditing(null);
    },
    onError: (err: any) =>
      toast({
        title: "Save failed",
        description: err?.message ?? "Try again.",
        variant: "destructive",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/cancellation-policies/${id}`);
      return res.text();
    },
    onSuccess: () => {
      toast({ title: "Deleted" });
      queryClient.invalidateQueries({ queryKey: ["/api/cancellation-policies"] });
      setConfirmDelete(null);
    },
    onError: (err: any) =>
      toast({
        title: "Delete failed",
        description: err?.message ?? "Try again.",
        variant: "destructive",
      }),
  });

  const onSave = () => {
    if (!form.name.trim() || !form.body.trim()) {
      toast({
        title: "Missing fields",
        description: "Name and body are required.",
        variant: "destructive",
      });
      return;
    }
    saveMutation.mutate({ ...form, id: editing?.id });
  };

  const toggleClientType = (name: string) => {
    setForm((f) => {
      const exists = f.defaultForClientTypes.includes(name);
      return {
        ...f,
        defaultForClientTypes: exists
          ? f.defaultForClientTypes.filter((t) => t !== name)
          : [...f.defaultForClientTypes, name],
      };
    });
  };

  return (
    <>
      <Card data-testid="card-cancellation-policies">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5" />
                Cancellation Policies
              </CardTitle>
              <CardDescription>
                Reusable cancellation policy templates that get snapshotted
                onto runs and bookings at create time. Editing a policy here
                does not change contracts already signed.
              </CardDescription>
            </div>
            <Button
              size="sm"
              onClick={openCreate}
              data-testid="button-create-policy"
            >
              <Plus className="w-4 h-4 mr-2" />
              New policy
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : policies.length === 0 ? (
            <p
              className="text-sm text-muted-foreground py-6 text-center"
              data-testid="text-no-policies"
            >
              No policies yet. Create one to use as the default on new
              bookings.
            </p>
          ) : (
            <ul className="space-y-3">
              {policies.map((p) => (
                <li
                  key={p.id}
                  className="border rounded-lg p-4 flex items-start justify-between gap-4"
                  data-testid={`row-policy-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{p.name}</span>
                      {p.isDefault && (
                        <Badge variant="secondary" className="gap-1">
                          <Star className="w-3 h-3" />
                          Workspace default
                        </Badge>
                      )}
                      {p.isActive === false && (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                      {p.defaultForClientTypes?.map((ct) => (
                        <Badge key={ct} variant="outline">
                          {ct}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2 whitespace-pre-wrap">
                      {p.body}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => openEdit(p)}
                      data-testid={`button-edit-policy-${p.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setConfirmDelete(p)}
                      data-testid={`button-delete-policy-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={creating || editing !== null}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit cancellation policy" : "New cancellation policy"}
            </DialogTitle>
            <DialogDescription>
              The body is copied onto each run / booking at create time. Use
              the client-type tags below to auto-pick this policy on the
              booking dialog.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="policy-name">Name</Label>
              <Input
                id="policy-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Standard, Senior Living, Festival, etc."
                data-testid="input-policy-name"
              />
            </div>
            <div>
              <Label htmlFor="policy-body">Body</Label>
              <Textarea
                id="policy-body"
                rows={10}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="If client cancels with less than 30 days notice…"
                data-testid="textarea-policy-body"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Workspace default</Label>
                <p className="text-xs text-muted-foreground">
                  Used when no client-type-specific default matches.
                </p>
              </div>
              <Switch
                checked={form.isDefault}
                onCheckedChange={(v) => setForm({ ...form, isDefault: v })}
                data-testid="switch-policy-default"
              />
            </div>

            {clientTypes.length > 0 && (
              <div>
                <Label className="text-base">
                  Default for client types (optional)
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  When the booking's client type matches one of these, this
                  policy auto-fills.
                </p>
                <div className="flex flex-wrap gap-2">
                  {clientTypes.map((ct) => {
                    const active = form.defaultForClientTypes.includes(ct.name);
                    return (
                      <button
                        type="button"
                        key={ct.id}
                        onClick={() => toggleClientType(ct.name)}
                        className={`px-3 py-1 text-sm rounded-full border ${
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border hover:bg-muted"
                        }`}
                        data-testid={`chip-client-type-${ct.id}`}
                      >
                        {ct.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label className="text-base">Active</Label>
              <Switch
                checked={form.isActive}
                onCheckedChange={(v) => setForm({ ...form, isActive: v })}
                data-testid="switch-policy-active"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreating(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={saveMutation.isPending}
              data-testid="button-save-policy"
            >
              {saveMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this policy?</AlertDialogTitle>
            <AlertDialogDescription>
              Bookings already signed with "{confirmDelete?.name}" keep their
              snapshot of the policy text — only future bookings are affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmDelete && deleteMutation.mutate(confirmDelete.id)
              }
              data-testid="button-confirm-delete-policy"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
